import { ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from './Button'
import { SectionProgress } from './SectionProgress'
import { useOnboardingStore } from '../store/onboardingStore'
import { usesAppLook } from '../lib/variant'
import { HeaderWash, V2 } from './v2'

interface StepContainerProps {
  title: string
  subtitle?: string
  children: ReactNode
  onContinue?: () => void
  continueDisabled?: boolean
  continueLabel?: string
  showContinue?: boolean
  showBack?: boolean
  hideProgress?: boolean
}

export function StepContainer({
  title,
  subtitle,
  children,
  onContinue,
  continueDisabled = false,
  continueLabel = 'Continue',
  showContinue = true,
  showBack = true,
  hideProgress = false,
}: StepContainerProps) {
  const { currentStep, goBack, getCurrentSection, navigationDirection } = useOnboardingStore()
  // organic-v2 arm only: app 4.33 look (header wash, slate type). Every control
  // className below is the unchanged original string.
  const v2 = usesAppLook()

  const variants = {
    initial: {
      opacity: 0,
      x: navigationDirection === 'forward' ? 20 : -20,
    },
    animate: {
      opacity: 1,
      x: 0,
    },
    exit: {
      opacity: 0,
      x: navigationDirection === 'forward' ? -20 : 20,
    },
  }

  return (
    <div className={v2 ? `min-h-dvh flex flex-col relative ${V2.page}` : 'min-h-dvh flex flex-col bg-white'}>
      {v2 && <HeaderWash />}
      {!hideProgress && (
        <div className={v2 ? 'w-full relative z-10' : 'w-full bg-white border-b border-gray-100'}>
          <div className="max-w-[600px] mx-auto px-6 py-4 flex items-center gap-4">
            {showBack && currentStep > 0 && (
              <button
                onClick={goBack}
                className={v2
                  ? 'w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/70 transition-colors -ml-2'
                  : 'w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors -ml-2'}
              >
                <svg className={v2 ? 'w-6 h-6 text-slate-600' : 'w-6 h-6 text-gray-600'} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <div className="flex-1">
              <SectionProgress currentSection={getCurrentSection()} />
            </div>
            {showBack && currentStep > 0 && <div className="w-10" />}
          </div>
        </div>
      )}

      <div className={v2 ? 'flex-1 flex flex-col relative z-10' : 'flex-1 flex flex-col'}>
        <div className="max-w-[600px] w-full mx-auto px-6 py-8 safe-area-bottom flex-1 flex flex-col">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              variants={variants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.2 }}
              className="flex-1 flex flex-col"
            >
              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={v2 ? `${V2.h1} text-center mb-4` : 'text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-4'}
              >
                {title}
              </motion.h1>

              {subtitle && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className={v2 ? `text-base ${V2.textSecondary} text-center mb-8` : 'text-base text-gray-500 text-center mb-8'}
                >
                  {subtitle}
                </motion.p>
              )}

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15 }}
                className="flex-1"
              >
                {children}
              </motion.div>

              {showContinue && (
                <div className="mt-8">
                  <Button
                    onClick={onContinue}
                    disabled={continueDisabled}
                  >
                    {continueLabel}
                  </Button>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}
